import React, { Component } from "react";
import { MDBDataTable } from "mdbreact";
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  Container,
  Badge,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import {
  getBannerList,
  toggleBannerStatus,
  deleteBanner,
  reorderBanners,
  getBannerAnalytics,
} from "../../Endpoint/Admin/index";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import { Link } from "react-router-dom";
import ConfirmModal from "../../components/Common/ConfirmModal";

// Helper to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return "https://via.placeholder.com/80x45?text=No+Image";
  if (imageUrl.startsWith("http")) return imageUrl;
  return `${process.env.REACT_APP_DEFAULTAUTH}${imageUrl}`;
};

class BannerList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      isLoaded: false,
      statusFilter: "",
      analyticsModal: false,
      analyticsData: null,
      // Search and pagination
      searchQuery: "",
      currentPage: 1,
      limit: 10,
      pagination: null,
      // Modal states
      showToggleModal: false,
      showDeleteModal: false,
      selectedBannerId: null,
      selectedBannerStatus: null,
      actionLoading: false,
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Banners", link: "#" },
      ],
      tableData: {
        columns: [
          { label: "Order", field: "order", sort: "asc", width: 60 },
          { label: "Image", field: "image", sort: "disabled", width: 100 },
          { label: "Title", field: "title", sort: "asc", width: 200 },
          { label: "Link Type", field: "linkType", sort: "asc", width: 100 },
          { label: "Clicks/Views", field: "stats", sort: "asc", width: 120 },
          { label: "Status", field: "status", sort: "asc", width: 80 },
          { label: "Actions", field: "actions", sort: "disabled", width: 150 },
        ],
        rows: [],
      },
    };
    this.searchTimeout = null;
  }

  componentDidMount() {
    this.getAllBanners();
  }

  getAllBanners = () => {
    const { statusFilter, searchQuery, currentPage, limit } = this.state;
    const params = {
      page: currentPage,
      limit: limit,
    };
    if (statusFilter) params.status = statusFilter;
    if (searchQuery.trim()) params.search = searchQuery.trim();

    getBannerList(params)
      .then((result) => {
        if (result.status === 200) {
          const rows = result.data.map((banner, index) => this.formatBannerRow(banner, index));
          this.setState({
            isLoaded: true,
            items: result.data,
            pagination: result.pagination,
            tableData: { ...this.state.tableData, rows },
          });
        } else {
          toast.error(result.message || "Failed to fetch banners");
        }
      })
      .catch(() => toast.error("Error fetching banners"));
  };

  handleSearchChange = (e) => {
    const searchQuery = e.target.value;
    this.setState({ searchQuery, currentPage: 1 });

    // Debounce search
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.getAllBanners();
    }, 500);
  };

  handlePageChange = (page) => {
    this.setState({ currentPage: page }, () => {
      this.getAllBanners();
    });
  };

  handleLimitChange = (e) => {
    this.setState({ limit: parseInt(e.target.value), currentPage: 1 }, () => {
      this.getAllBanners();
    });
  };

  formatBannerRow = (banner, index) => {
    const ctr = banner.impressionCount > 0
      ? ((banner.clickCount / banner.impressionCount) * 100).toFixed(1)
      : 0;

    const getLinkTypeBadge = (type) => {
      const colors = {
        none: "secondary",
        coupon: "success",
        external: "info",
        internal: "primary",
      };
      return <Badge color={colors[type] || "secondary"}>{type}</Badge>;
    };

    // Searchable text for MDBDataTable
    const searchText = `${banner.title} ${banner.description || ''} ${banner.linkType} ${banner.isActive ? 'active' : 'inactive'}`.toLowerCase();

    return {
      // Hidden searchable field
      searchField: searchText,
      order: (
        <span className="font-weight-bold text-muted">
          #{banner.displayOrder + 1}
        </span>
      ),
      image: (
        <img
          src={getImageUrl(banner.imageUrl)}
          alt={banner.title}
          className="rounded"
          style={{
            width: "80px",
            height: "45px",
            objectFit: "cover",
            border: "1px solid #ddd",
          }}
          onError={(e) => {
            e.target.src = "https://via.placeholder.com/80x45?text=No+Image";
          }}
        />
      ),
      title: (
        <div>
          <span className="font-weight-medium">{banner.title}</span>
          {banner.description && (
            <small className="d-block text-muted" style={{ maxWidth: "150px" }}>
              {banner.description.length > 30
                ? `${banner.description.substring(0, 30)}...`
                : banner.description}
            </small>
          )}
        </div>
      ),
      linkType: (
        <div>
          {getLinkTypeBadge(banner.linkType)}
          {banner.linkType === "coupon" && banner.linkedCouponId && (
            <small className="d-block text-success mt-1">
              {banner.linkedCouponId.code}
            </small>
          )}
        </div>
      ),
      stats: (
        <div>
          {banner.linkType === "none" ? (
            // For non-clickable banners, only show views
            <>
              <span className="font-weight-medium">
                {banner.impressionCount.toLocaleString()} views
              </span>
              <small className="d-block text-muted">No click action</small>
            </>
          ) : (
            // For clickable banners, show clicks/views and CTR
            <>
              <span className="font-weight-medium">
                {banner.clickCount.toLocaleString()} / {banner.impressionCount.toLocaleString()}
              </span>
              <div className="progress mt-1" style={{ height: "4px" }}>
                <div
                  className={`progress-bar ${parseFloat(ctr) >= 5 ? "bg-success" : parseFloat(ctr) >= 2 ? "bg-warning" : "bg-danger"}`}
                  style={{ width: `${Math.min(ctr * 10, 100)}%` }}
                />
              </div>
              <small className="text-muted">CTR: {ctr}%</small>
            </>
          )}
        </div>
      ),
      status: banner.isActive ? (
        <Badge color="success">Active</Badge>
      ) : (
        <Badge color="danger">Inactive</Badge>
      ),
      actions: (
        <div className="d-flex">
          <Link to={`/banner/edit/${banner._id}`}>
            <Button
              color="primary"
              size="sm"
              className="mr-1 btn-icon"
              title="Edit Banner"
              style={{ minWidth: "32px", padding: "4px 8px" }}
            >
              <i className="ri-edit-line" style={{ fontSize: "14px" }}></i>
            </Button>
          </Link>
          <Button
            color={banner.isActive ? "warning" : "success"}
            size="sm"
            className="mr-1 btn-icon"
            onClick={() => this.handleToggleStatus(banner._id, banner.isActive)}
            title={banner.isActive ? "Deactivate" : "Activate"}
            style={{ minWidth: "32px", padding: "4px 8px" }}
          >
            <i
              className={banner.isActive ? "ri-pause-line" : "ri-play-line"}
              style={{ fontSize: "14px" }}
            ></i>
          </Button>
          <Button
            color="danger"
            size="sm"
            className="btn-icon"
            onClick={() => this.handleDelete(banner._id)}
            title="Delete Banner"
            style={{ minWidth: "32px", padding: "4px 8px" }}
          >
            <i className="ri-delete-bin-line" style={{ fontSize: "14px" }}></i>
          </Button>
        </div>
      ),
    };
  };

  // Modal toggle handlers
  toggleToggleModal = () => this.setState({ showToggleModal: !this.state.showToggleModal });
  toggleDeleteModal = () => this.setState({ showDeleteModal: !this.state.showDeleteModal });

  handleToggleStatus = (id, currentStatus) => {
    this.setState({ selectedBannerId: id, selectedBannerStatus: currentStatus, showToggleModal: true });
  };

  confirmToggleStatus = () => {
    const { selectedBannerId, selectedBannerStatus } = this.state;
    const action = selectedBannerStatus ? "deactivate" : "activate";
    this.setState({ actionLoading: true });
    toggleBannerStatus(selectedBannerId)
      .then((response) => {
        if (response.status === 200) {
          toast.success(`Banner ${action}d successfully`);
          this.getAllBanners();
        } else {
          toast.error(response.message || `Failed to ${action} banner`);
        }
      })
      .catch(() => toast.error(`Error ${action}ing banner`))
      .finally(() => {
        this.setState({ actionLoading: false, showToggleModal: false, selectedBannerId: null, selectedBannerStatus: null });
      });
  };

  handleDelete = (id) => {
    this.setState({ selectedBannerId: id, showDeleteModal: true });
  };

  confirmDelete = () => {
    const { selectedBannerId } = this.state;
    this.setState({ actionLoading: true });
    deleteBanner(selectedBannerId)
      .then((response) => {
        if (response.status === 200) {
          toast.success("Banner deleted successfully");
          this.getAllBanners();
          // Also refresh analytics data
          this.loadAnalytics();
        } else {
          toast.error(response.message || "Failed to delete banner");
        }
      })
      .catch(() => toast.error("Error deleting banner"))
      .finally(() => {
        this.setState({ actionLoading: false, showDeleteModal: false, selectedBannerId: null });
      });
  };

  handleFilterChange = (e) => {
    this.setState({ [e.target.name]: e.target.value, currentPage: 1 }, () => {
      this.getAllBanners();
    });
  };

  toggleAnalyticsModal = () => {
    const { analyticsModal } = this.state;
    if (!analyticsModal) {
      this.loadAnalytics();
    }
    this.setState({ analyticsModal: !analyticsModal });
  };

  loadAnalytics = () => {
    getBannerAnalytics()
      .then((response) => {
        if (response.status === 200) {
          this.setState({ analyticsData: response.data });
        } else {
          toast.error("Failed to load analytics");
        }
      })
      .catch(() => toast.error("Error loading analytics"));
  };

  render() {
    const { statusFilter, analyticsModal, analyticsData } = this.state;

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Banner Management" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Analytics Summary Cards */}
            {analyticsData && (
              <Row className="mb-4">
                <Col md={3}>
                  <Card className="mini-stat bg-primary text-white">
                    <CardBody>
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5 className="text-white">{analyticsData.overall.totalBanners}</h5>
                          <p className="mb-0">Total Banners</p>
                        </div>
                        <i className="ri-image-line" style={{ fontSize: "32px", opacity: 0.5 }}></i>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="mini-stat bg-success text-white">
                    <CardBody>
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5 className="text-white">{analyticsData.overall.activeBanners}</h5>
                          <p className="mb-0">Active Banners</p>
                        </div>
                        <i className="ri-checkbox-circle-line" style={{ fontSize: "32px", opacity: 0.5 }}></i>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="mini-stat bg-info text-white">
                    <CardBody>
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5 className="text-white">{analyticsData.overall.totalImpressions.toLocaleString()}</h5>
                          <p className="mb-0">Total Views</p>
                        </div>
                        <i className="ri-eye-line" style={{ fontSize: "32px", opacity: 0.5 }}></i>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={3}>
                  <Card className="mini-stat bg-warning text-white">
                    <CardBody>
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5 className="text-white">{analyticsData.overall.overallCTR}%</h5>
                          <p className="mb-0">Overall CTR</p>
                        </div>
                        <i className="ri-cursor-line" style={{ fontSize: "32px", opacity: 0.5 }}></i>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}

            <Row>
              <Col xs={12}>
                <Card>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h4 className="card-title mb-0">
                        <i className="ri-image-2-line mr-2"></i>
                        Banners
                      </h4>
                      <div>
                        <Button
                          color="info"
                          className="mr-2"
                          onClick={this.toggleAnalyticsModal}
                        >
                          <i className="ri-bar-chart-line mr-1"></i> Analytics
                        </Button>
                        <Link to="/banner/create">
                          <Button color="primary">
                            <i className="ri-add-line mr-1"></i> Create Banner
                          </Button>
                        </Link>
                      </div>
                    </div>

                    {/* Search and Filters */}
                    <Row className="mb-4">
                      <Col md={4}>
                        <FormGroup>
                          <Label>Search</Label>
                          <div className="position-relative">
                            <Input
                              type="text"
                              placeholder="Search by title or description..."
                              value={this.state.searchQuery}
                              onChange={this.handleSearchChange}
                              style={{ paddingLeft: "35px" }}
                            />
                            <i
                              className="ri-search-line"
                              style={{
                                position: "absolute",
                                left: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#adb5bd",
                              }}
                            ></i>
                          </div>
                        </FormGroup>
                      </Col>
                      <Col md={3}>
                        <FormGroup>
                          <Label>Status</Label>
                          <Input
                            type="select"
                            name="statusFilter"
                            value={statusFilter}
                            onChange={this.handleFilterChange}
                          >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={2}>
                        <FormGroup>
                          <Label>Show entries</Label>
                          <Input
                            type="select"
                            value={this.state.limit}
                            onChange={this.handleLimitChange}
                          >
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={3} className="d-flex align-items-end">
                        {this.state.pagination && (
                          <p className="text-muted mb-3">
                            Showing {((this.state.currentPage - 1) * this.state.limit) + 1} to{" "}
                            {Math.min(this.state.currentPage * this.state.limit, this.state.pagination.totalCount)} of{" "}
                            {this.state.pagination.totalCount} entries
                          </p>
                        )}
                      </Col>
                    </Row>

                    <MDBDataTable
                      responsive
                      bordered
                      striped
                      hover
                      noBottomColumns
                      data={this.state.tableData}
                      searching={false}
                      paging={false}
                      className="banner-table"
                    />

                    {/* Custom Pagination */}
                    {this.state.pagination && this.state.pagination.totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <p className="text-muted mb-0">
                          Page {this.state.pagination.currentPage} of {this.state.pagination.totalPages}
                        </p>
                        <nav>
                          <ul className="pagination mb-0">
                            <li className={`page-item ${!this.state.pagination.hasPrevPage ? "disabled" : ""}`}>
                              <button
                                className="page-link"
                                onClick={() => this.handlePageChange(1)}
                                disabled={!this.state.pagination.hasPrevPage}
                              >
                                <i className="ri-skip-back-line"></i>
                              </button>
                            </li>
                            <li className={`page-item ${!this.state.pagination.hasPrevPage ? "disabled" : ""}`}>
                              <button
                                className="page-link"
                                onClick={() => this.handlePageChange(this.state.currentPage - 1)}
                                disabled={!this.state.pagination.hasPrevPage}
                              >
                                <i className="ri-arrow-left-s-line"></i>
                              </button>
                            </li>
                            {/* Page numbers */}
                            {Array.from({ length: Math.min(5, this.state.pagination.totalPages) }, (_, i) => {
                              let pageNum;
                              const currentPage = this.state.pagination.currentPage;
                              const totalPages = this.state.pagination.totalPages;

                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }

                              return (
                                <li key={pageNum} className={`page-item ${currentPage === pageNum ? "active" : ""}`}>
                                  <button
                                    className="page-link"
                                    onClick={() => this.handlePageChange(pageNum)}
                                  >
                                    {pageNum}
                                  </button>
                                </li>
                              );
                            })}
                            <li className={`page-item ${!this.state.pagination.hasNextPage ? "disabled" : ""}`}>
                              <button
                                className="page-link"
                                onClick={() => this.handlePageChange(this.state.currentPage + 1)}
                                disabled={!this.state.pagination.hasNextPage}
                              >
                                <i className="ri-arrow-right-s-line"></i>
                              </button>
                            </li>
                            <li className={`page-item ${!this.state.pagination.hasNextPage ? "disabled" : ""}`}>
                              <button
                                className="page-link"
                                onClick={() => this.handlePageChange(this.state.pagination.totalPages)}
                                disabled={!this.state.pagination.hasNextPage}
                              >
                                <i className="ri-skip-forward-line"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Analytics Modal */}
            <Modal isOpen={analyticsModal} toggle={this.toggleAnalyticsModal} size="lg">
              <ModalHeader toggle={this.toggleAnalyticsModal}>
                <i className="ri-bar-chart-line mr-2"></i>
                Banner Analytics
              </ModalHeader>
              <ModalBody>
                {analyticsData ? (
                  <>
                    {/* Overall Stats */}
                    <Row className="mb-4">
                      <Col md={3} className="text-center">
                        <h3 className="text-primary">{analyticsData.overall.totalBanners}</h3>
                        <small className="text-muted">Total Banners</small>
                      </Col>
                      <Col md={3} className="text-center">
                        <h3 className="text-success">{analyticsData.overall.activeBanners}</h3>
                        <small className="text-muted">Active</small>
                      </Col>
                      <Col md={3} className="text-center">
                        <h3 className="text-info">{analyticsData.overall.totalClicks.toLocaleString()}</h3>
                        <small className="text-muted">Total Clicks</small>
                      </Col>
                      <Col md={3} className="text-center">
                        <h3 className="text-warning">{analyticsData.overall.overallCTR}%</h3>
                        <small className="text-muted">Overall CTR</small>
                      </Col>
                    </Row>

                    {/* Per Banner Stats */}
                    <h6 className="mb-3">Performance by Banner</h6>
                    <div className="table-responsive">
                      <table className="table table-bordered table-sm">
                        <thead className="thead-light">
                          <tr>
                            <th>Banner</th>
                            <th>Impressions</th>
                            <th>Clicks</th>
                            <th>CTR</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.banners.map((banner) => (
                            <tr key={banner._id}>
                              <td>
                                <div className="d-flex align-items-center">
                                  <img
                                    src={getImageUrl(banner.imageUrl)}
                                    alt=""
                                    className="rounded mr-2"
                                    style={{ width: "40px", height: "25px", objectFit: "cover" }}
                                  />
                                  {banner.title}
                                </div>
                              </td>
                              <td>{banner.impressionCount.toLocaleString()}</td>
                              <td>{banner.clickCount.toLocaleString()}</td>
                              <td>
                                <Badge
                                  color={parseFloat(banner.ctr) >= 5 ? "success" : parseFloat(banner.ctr) >= 2 ? "warning" : "danger"}
                                >
                                  {banner.ctr}%
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4">
                    <i className="ri-loader-4-line ri-2x spin"></i>
                    <p className="mt-2">Loading analytics...</p>
                  </div>
                )}
              </ModalBody>
            </Modal>
          </Container>
        </div>

        {/* Toggle Status Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showToggleModal}
          toggle={this.toggleToggleModal}
          onConfirm={this.confirmToggleStatus}
          title={this.state.selectedBannerStatus ? "Deactivate Banner" : "Activate Banner"}
          message={`Are you sure you want to ${this.state.selectedBannerStatus ? "deactivate" : "activate"} this banner?`}
          confirmText={this.state.selectedBannerStatus ? "Deactivate" : "Activate"}
          confirmColor={this.state.selectedBannerStatus ? "warning" : "success"}
          icon={this.state.selectedBannerStatus ? "ri-toggle-line" : "ri-toggle-fill"}
          loading={this.state.actionLoading}
        />

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showDeleteModal}
          toggle={this.toggleDeleteModal}
          onConfirm={this.confirmDelete}
          title="Delete Banner"
          message="Are you sure you want to delete this banner? This action cannot be undone."
          confirmText="Delete"
          confirmColor="danger"
          icon="ri-delete-bin-line"
          loading={this.state.actionLoading}
        />

        <ToastContainer />
      </React.Fragment>
    );
  }
}

export default BannerList;
